/**
 * Vague 1 · Phase C.4 · Sync manager.
 *
 * Orchestre :
 *  1. `enqueueOrSend(kind, payload, meta)` : utilisé par les mutations UI.
 *     - Si online : POST direct. Si succès → return result. Si erreur réseau
 *       (ApiTransportError) → enqueue dans outbox + retourne null (l'UI
 *       affiche un placeholder optimiste).
 *     - Si offline : enqueue + retourne null direct.
 *  2. `flushOutbox()` : rejoue toutes les ops pending en FIFO. Une op qui
 *     échoue avec un 4xx non-transitoire (400/403/404/409/422) est marquée
 *     `failed` et le flush continue (sinon une op coincée bloque tout).
 *     Les erreurs transport (réseau toujours down) stoppent le flush :
 *     on retentera au prochain `online` event.
 *  3. Boot : démarre le scheduler (online listener + tentative initiale
 *     1s après boot pour laisser l'auth s'établir).
 */
import { ApiClientError, ApiTransportError } from "@victor/api-client";
import { api } from "./api";
import {
  enqueueOutbox,
  listOutbox,
  deleteOutbox,
  updateOutbox,
  type OutboxOp,
  type OutboxOpKind,
} from "./outbox";
import { notifyOutboxChanged } from "./network";

// ---------------------------------------------------------------------------
// Enqueue ou envoie
// ---------------------------------------------------------------------------

export type EnqueueResult<T> =
  | { kind: "sent"; result: T }
  | { kind: "queued"; opId: string };

export async function enqueueOrSend<T>(
  opKind: OutboxOpKind,
  payload: unknown,
  meta: { vacationId?: string } | undefined,
  sendFn: () => Promise<T>,
): Promise<EnqueueResult<T>> {
  // Tentative directe d'abord (même si navigator.onLine ment, on tente).
  try {
    const result = await sendFn();
    return { kind: "sent", result };
  } catch (err) {
    // Erreur applicative typée : on ne queue PAS (le retry ne changera rien).
    if (err instanceof ApiClientError) throw err;
    // Erreur réseau : on enqueue
    if (err instanceof ApiTransportError) {
      const op = await enqueueOutbox({ kind: opKind, payload, meta });
      notifyOutboxChanged();
      return { kind: "queued", opId: op.id };
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

/** Codes API qui ne réussiront jamais en rejouant (don't retry). */
const NON_TRANSIENT_HTTP_STATUS = new Set([400, 401, 403, 404, 409, 422]);

export interface FlushResult {
  attempted: number;
  sent: number;
  failed: number;
  stillPending: number;
}

let flushInFlight: Promise<FlushResult> | null = null;

export function flushOutbox(): Promise<FlushResult> {
  if (flushInFlight) return flushInFlight;
  flushInFlight = doFlush().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

async function doFlush(): Promise<FlushResult> {
  const ops = await listOutbox();
  const pending = ops.filter((o) => o.status === "pending");

  let sent = 0;
  let failed = 0;
  let attempted = 0;

  for (const op of pending) {
    attempted++;
    try {
      await replayOp(op);
      await deleteOutbox(op.id);
      sent++;
    } catch (err) {
      if (err instanceof ApiTransportError) {
        // Réseau toujours down → on stoppe net, on retentera plus tard
        await updateOutbox(op.id, {
          attempts: op.attempts + 1,
          lastError: "Réseau indisponible",
        });
        break;
      }
      if (err instanceof ApiClientError) {
        if (NON_TRANSIENT_HTTP_STATUS.has(err.status)) {
          await updateOutbox(op.id, {
            attempts: op.attempts + 1,
            status: "failed",
            lastError: `${err.code}: ${err.message}`,
          });
          failed++;
          continue;
        }
        // 5xx / 502 / 503 → réessayer plus tard
        await updateOutbox(op.id, {
          attempts: op.attempts + 1,
          lastError: `${err.code}: ${err.message}`,
        });
        break;
      }
      // Cas inattendu : on stoppe, on retentera
      await updateOutbox(op.id, {
        attempts: op.attempts + 1,
        lastError: err instanceof Error ? err.message : "Erreur inconnue",
      });
      break;
    }
  }

  const stillPending = (await listOutbox()).filter(
    (o) => o.status === "pending",
  ).length;

  if (attempted > 0) notifyOutboxChanged();

  return { attempted, sent, failed, stillPending };
}

async function replayOp(op: OutboxOp): Promise<unknown> {
  switch (op.kind) {
    case "pointage":
      return api.me.pointage(op.payload as Parameters<typeof api.me.pointage>[0]);
    case "main_courante":
      return api.me.mainCourante(
        op.payload as Parameters<typeof api.me.mainCourante>[0],
      );
    case "ronde_scan":
      return api.me.scanRonde(
        op.payload as Parameters<typeof api.me.scanRonde>[0],
      );
    default: {
      // Exhaustivité TypeScript
      const _exhaustive: never = op.kind;
      throw new Error(`Unknown outbox op kind: ${String(_exhaustive)}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

let started = false;

export function startSyncScheduler(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  // Tentative initiale (laisse 1s pour que l'auth s'établisse au boot)
  window.setTimeout(() => {
    void flushOutbox();
  }, 1000);

  // À chaque retour réseau
  window.addEventListener("online", () => {
    void flushOutbox();
  });

  // Au focus de l'onglet : utile si l'agent a switch d'app et revient
  window.addEventListener("focus", () => {
    void flushOutbox();
  });
}
